import React, { useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import { useJWT } from '../contexts/JWTProvider';
import { ServiceThemeOptions } from '../themes';
import {
  createDarkenedColor,
  createHoverBackground,
} from '../utils/ThemeUtils';
import { createAnalyticsContext } from '../utils/analytics';
import {
  OTHER_REASON,
  PROVIDER_REASON,
  SKIP_FEEDBACK_REASONS,
  SkipFeedbackMeta,
  SkipFeedbackPayload,
  SkipReasonValue,
  buildSkipFeedbackPayload,
  isValidEmail,
  submitSkipFeedback,
} from '../utils/skipFeedback';

interface SkipConfirmationProps {
  open: boolean;
  onClose: () => void;
  onSkip: (
    payload: SkipFeedbackPayload,
    meta: SkipFeedbackMeta
  ) => Promise<void> | void;
  serviceName: string;
}

const SkipConfirmation: React.FC<SkipConfirmationProps> = ({
  open,
  onClose,
  onSkip,
  serviceName,
}) => {
  const jwt = useJWT();
  const theme = useTheme() as ServiceThemeOptions;
  const primaryColor = (theme.palette?.primary as any)?.main || '#B60207';
  const textPrimaryColor = (theme.palette?.text as any)?.primary || '#263238';
  const textSecondaryColor =
    (theme.palette?.text as any)?.secondary || '#586166';
  const dividerColor = theme.palette?.divider || '#DCDEDF';
  const paperBgColor = theme.palette?.background?.paper || '#FFFFFF';
  const fontFamily =
    (theme.typography as any)?.fontFamily || 'Inter, sans-serif';

  // Get value proposition from theme or use fallback
  const valueProp =
    theme.custom?.skipConfirmation?.valueProp ||
    "Connecting your telematics data unlocks services tailored to your fleet's needs — from smarter operations to improved bottom-line results.";

  // Use utility functions for hover colors
  const hoverBackground = createHoverBackground(primaryColor, 0.04);
  const darkenedPrimaryColor = createDarkenedColor(primaryColor);

  const [selectedReason, setSelectedReason] = useState<SkipReasonValue | ''>(
    ''
  );
  const [details, setDetails] = useState('');
  const [contactRequested, setContactRequested] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactEmailError, setContactEmailError] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedReason('');
      setDetails('');
      setContactRequested(false);
      setContactName('');
      setContactEmail('');
      setContactEmailError('');
    }
  }, [open]);

  const inputStyles = useMemo(
    () => ({
      width: '100%',
      mb: 2,
      '& .MuiOutlinedInput-root': {
        minHeight: '56px',
        borderRadius: '4px',
        fontFamily,
        '& fieldset': {
          borderColor: theme.palette?.grey?.[300] || '#C7CACB',
        },
        '&:hover fieldset': {
          borderColor: theme.palette?.text?.primary || '#263238',
        },
        '&.Mui-focused fieldset': {
          borderColor:
            theme.custom?.inputField?.focus?.borderColor || primaryColor,
          borderWidth: theme.custom?.inputField?.focus?.borderWidth || '2px',
        },
        '&.MuiInputBase-multiline': {
          minHeight: '96px',
          '& textarea': {
            padding: '12px 0',
          },
        },
      },
      '& .MuiInputLabel-root': {
        fontFamily,
      },
    }),
    [
      fontFamily,
      primaryColor,
      theme.custom?.inputField?.focus?.borderColor,
      theme.custom?.inputField?.focus?.borderWidth,
      theme.palette?.grey,
      theme.palette?.text?.primary,
    ]
  );

  const handleReasonChange = (event: SelectChangeEvent<unknown>) => {
    const nextValue = event.target.value as SkipReasonValue | '';
    setSelectedReason(nextValue);
    if (nextValue !== PROVIDER_REASON && nextValue !== OTHER_REASON) {
      setDetails('');
    }
  };

  const reasonLabel = useMemo(() => {
    return SKIP_FEEDBACK_REASONS.find(
      (reason) => reason.value === selectedReason
    )?.label;
  }, [selectedReason]);

  const handleSkip = async () => {
    if (contactRequested) {
      const trimmedEmail = contactEmail.trim();
      if (trimmedEmail && !isValidEmail(trimmedEmail)) {
        setContactEmailError('Enter a valid email address.');
        return;
      }
    }

    setContactEmailError('');

    amplitude.track('User Confirmed Skip ELD Setup', {
      serviceName,
      timestamp: new Date().toISOString(),
    });
    const { payload, meta } = buildSkipFeedbackPayload({
      reasonValue: selectedReason,
      reasonLabel,
      details,
      contactRequested,
      contactName,
      contactEmail,
      defaultMessage: 'User skipped linking without sharing a reason.',
    });

    const analyticsContext = createAnalyticsContext(jwt, serviceName);
    const tokenForFeedback = analyticsContext.token || jwt.rawToken || null;

    await submitSkipFeedback(tokenForFeedback, payload);

    await onSkip(payload, meta);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      fullWidth
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 32px)', sm: '420px' },
          maxWidth: { xs: 'calc(100vw - 32px)', sm: '420px' },
          margin: { xs: 2, sm: 'auto' },
          borderRadius: '4px',
          backgroundColor: paperBgColor,
          boxShadow:
            '0px 4px 8px rgba(0, 0, 0, 0.12), 0px 6px 12px rgba(0, 0, 0, 0.12), 0px 1px 16px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${dividerColor}`,
          padding: { xs: '16px 16px', sm: '18px 24px' },
          fontSize: { xs: '18px', sm: '21.2px' },
          fontWeight: 600,
          letterSpacing: '-0.15px',
          position: 'relative',
          color: textPrimaryColor,
          pr: { xs: '48px', sm: '56px' }, // Extra space for close button
        }}
      >
        Skip linking ELD telematics
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: { xs: 8, sm: 8 },
            top: { xs: 8, sm: 8 },
            width: { xs: '40px', sm: '40px' },
            height: { xs: '40px', sm: '40px' },
            color: textSecondaryColor,
            '&:hover': {
              backgroundColor: hoverBackground,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: { xs: '16px 16px 24px', sm: '16px 24px 24px' },
          minHeight: { xs: 'auto', sm: '160px' },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            lineHeight: { xs: '20px', sm: '24px' },
            letterSpacing: '0.15px',
            marginTop: { xs: '16px', sm: '24px' },
            marginBottom: { xs: '12px', sm: '16px' },
            color: textPrimaryColor,
          }}
        >
          Are you sure you want to skip connecting your ELD account to{' '}
          {serviceName}?
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            fontWeight: 700,
            lineHeight: { xs: '20px', sm: '24px' },
            letterSpacing: '0.15px',
            color: textPrimaryColor,
            marginTop: { xs: '12px', sm: '16px' },
            marginBottom: { xs: '12px', sm: '16px' },
          }}
        >
          {valueProp}
        </Typography>

        <TextField
          select
          fullWidth
          value={selectedReason}
          label='Reason (optional)'
          variant='outlined'
          sx={inputStyles}
          InputLabelProps={{
            shrink: true,
            sx: {
              fontFamily,
            },
          }}
          SelectProps={{
            displayEmpty: true,
            onChange: handleReasonChange,
            renderValue: (value) => {
              if (!value) {
                return (
                  <Typography component='span' color='text.secondary'>
                    Select a reason (optional)
                  </Typography>
                );
              }

              const reason = SKIP_FEEDBACK_REASONS.find(
                (item) => item.value === value
              );
              return reason?.label ?? String(value);
            },
          }}
        >
          <MenuItem value=''>
            <Typography variant='body2' color='text.secondary'>
              No reason
            </Typography>
          </MenuItem>
          {SKIP_FEEDBACK_REASONS.map((reason) => (
            <MenuItem key={reason.value} value={reason.value}>
              {reason.label}
            </MenuItem>
          ))}
        </TextField>

        {(selectedReason === PROVIDER_REASON ||
          selectedReason === OTHER_REASON) && (
          <TextField
            fullWidth
            value={details}
            onChange={(event) => setDetails(event.target.value)}
            label={
              selectedReason === PROVIDER_REASON
                ? 'Which provider were you looking for?'
                : 'Tell us more'
            }
            placeholder={
              selectedReason === PROVIDER_REASON
                ? 'Provider name'
                : 'Optional details'
            }
            variant='outlined'
            sx={inputStyles}
            multiline={selectedReason === OTHER_REASON}
            minRows={selectedReason === OTHER_REASON ? 2 : undefined}
          />
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={contactRequested}
              onChange={(event) => {
                const checked = event.target.checked;
                setContactRequested(checked);
                if (!checked) {
                  setContactName('');
                  setContactEmail('');
                  setContactEmailError('');
                }
              }}
              sx={{
                p: 0,
                mr: 1.5,
                color: textSecondaryColor,
                '&.Mui-checked': {
                  color: primaryColor,
                },
              }}
            />
          }
          label='Contact me'
          componentsProps={{
            typography: {
              sx: {
                fontFamily,
                color: textPrimaryColor,
              },
            },
          }}
          sx={{
            alignItems: 'center',
            width: '100%',
            mx: 0,
            mb: contactRequested ? 1 : 0,
          }}
        />

        {contactRequested && (
          <>
            <TextField
              fullWidth
              value={contactName}
              onChange={(event) => setContactName(event.target.value)}
              label='Name'
              variant='outlined'
              sx={inputStyles}
            />
            <TextField
              fullWidth
              value={contactEmail}
              onChange={(event) => {
                const value = event.target.value;
                setContactEmail(value);
                if (contactEmailError) {
                  if (!value.trim() || isValidEmail(value)) {
                    setContactEmailError('');
                  }
                }
              }}
              label='Email'
              variant='outlined'
              type='email'
              sx={inputStyles}
              error={!!contactEmailError}
              helperText={contactEmailError}
            />
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: { xs: '16px 16px 24px', sm: '16px 24px 24px' },
          gap: { xs: '8px', sm: '8px' },
          justifyContent: 'flex-end',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          onClick={handleSkip}
          sx={{
            fontSize: { xs: '16px', sm: '14px' },
            fontWeight: 700,
            letterSpacing: '0.75px',
            textTransform: 'uppercase',
            padding: { xs: '12px 16px', sm: '12px' },
            height: { xs: '48px', sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
            order: { xs: 2, sm: 1 },
            color: primaryColor,
            '&:hover': {
              backgroundColor: hoverBackground,
            },
          }}
        >
          SKIP FOR NOW
        </Button>
        <Button
          variant='contained'
          onClick={onClose}
          sx={{
            backgroundColor: primaryColor,
            color: (theme.custom?.buttonTextColor as string) || '#FFFFFF',
            fontSize: { xs: '16px', sm: '14px' },
            fontWeight: 700,
            letterSpacing: '0.75px',
            textTransform: 'uppercase',
            padding: { xs: '12px 16px', sm: '12px' },
            height: { xs: '48px', sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
            order: { xs: 1, sm: 2 },
            boxShadow:
              '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 8px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: darkenedPrimaryColor,
            },
          }}
        >
          CONNECT MY ELD
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkipConfirmation;
